import { NurseTrainingStatus } from "./models";

/**
 * Which training statuses count toward HPPD and PPD (hours and cost). One value instead of three loose booleans,
 * so every stats helper takes the same input; Regular and Trainer shifts always count. Coverage questions
 * ("is this required slot filled?") never read it — see `filterNursesForCoverage`.
 */
export interface TrainingPpdCounting {
	readonly countTraineeTowardsPpd: boolean;
	readonly countOrientationTowardsPpd: boolean;
	readonly countEducationTowardsPpd: boolean;
}

/** Trainee, Orientation and Education shifts all left out of HPPD/PPD; a department's value when all three switches are off. */
export const COUNT_NO_TRAINING: TrainingPpdCounting = Object.freeze({
	countTraineeTowardsPpd: false,
	countOrientationTowardsPpd: false,
	countEducationTowardsPpd: false,
});

/**
 * All three training statuses counted, whatever the department's switches say: the printouts' personal "count all
 * training shifts" setting when on, and callers that count every shift on purpose (the Availability widget, the
 * auto-staffing headcount).
 */
export const COUNT_ALL_TRAINING: TrainingPpdCounting = Object.freeze({
	countTraineeTowardsPpd: true,
	countOrientationTowardsPpd: true,
	countEducationTowardsPpd: true,
});

/** Whether a shift with this training status counts toward HPPD/PPD. No default case: a new status must decide here. */
export function countsTowardsPpd(counting: TrainingPpdCounting, status: NurseTrainingStatus): boolean {
	switch (status) {
		case NurseTrainingStatus.Trainee:
			return counting.countTraineeTowardsPpd;
		case NurseTrainingStatus.Orientation:
			return counting.countOrientationTowardsPpd;
		case NurseTrainingStatus.Education:
			return counting.countEducationTowardsPpd;
		case NurseTrainingStatus.Regular:
		case NurseTrainingStatus.Trainer:
			return true;
	}
	// Unreachable for typed data; a status from bad data fails loud instead of silently leaving the shift out.
	throw new Error(`Unhandled training status: ${status satisfies never}`);
}

/**
 * The department's three switches as a counting value. A fresh object holding only those three fields, so a caller
 * can't keep the whole department alive or read anything else through it.
 */
export function trainingPpdCountingOf(department: TrainingPpdCounting): TrainingPpdCounting {
	return {
		countTraineeTowardsPpd: department.countTraineeTowardsPpd,
		countOrientationTowardsPpd: department.countOrientationTowardsPpd,
		countEducationTowardsPpd: department.countEducationTowardsPpd,
	};
}

/**
 * What the printouts' DHPPD/NHPPD count, from the viewer's personal `printout_hppd_include_trainees` setting:
 * on counts all three training statuses, off follows the department's switches (like the Day view's HPPD).
 */
export function printoutTrainingCounting(hppdIncludeTrainees: boolean, department: TrainingPpdCounting): TrainingPpdCounting {
	return hppdIncludeTrainees ? COUNT_ALL_TRAINING : trainingPpdCountingOf(department);
}
